import { isPlayedResult, pointsFor, type Colour, type Score } from '../../model/types.js';
import { colourState, type PreferenceStrength } from '../colour.js';
import type { PairingPlayer, PairingRequest } from '../types.js';

export type Float = 'down' | 'up' | null;

/** Everything the Dutch rules need to know about one player for this round. */
export interface DutchPlayer {
  /** Pairing number (TPN). */
  readonly id: number;
  /** Position in the round's ranking (pairing score desc, TPN asc), 0-based. */
  idx: number;
  /** Pairing score: points plus this round's acceleration (tenths). */
  readonly score: Score;
  readonly rating: number;
  readonly colours: readonly Colour[];
  readonly colourDifference: number;
  readonly preference: Colour | null;
  readonly strength: PreferenceStrength;
  /** |colour difference| > 1. */
  readonly absoluteImbalance: boolean;
  /** The colour of the last two played games when they were the same. */
  readonly repeatedColour: Colour | null;
  /** Float received in the previous round and two rounds before. */
  readonly float1: Float;
  readonly float2: Float;
  /** May receive the pairing-allocated bye ([C2]). */
  readonly pabEligible: boolean;
  readonly playedGames: number;
  /** Opponents of played games, plus forbidden opponents ([C1]). */
  readonly cannotMeet: ReadonlySet<number>;
  readonly topscorer: boolean;
}

function scoreBefore(p: PairingPlayer, round: number, req: PairingRequest): Score {
  // Points after round-1 rounds, plus the acceleration of `round` (1-based).
  let s = 0;
  for (let r = 0; r < round - 1 && r < p.history.length; r++) {
    s += pointsFor((p.history[r] as PairingPlayer['history'][number]).result, req.pointSystem);
  }
  const acc =
    round === req.round ? (p.acceleration ?? 0) : (p.accelerationHistory?.[round - 1] ?? 0);
  return s + acc;
}

/**
 * The float a player received in round `round` (1-based, a past round), per
 * C.04.3 (2026) 1.4: a downfloat to the higher-ranked player of a pair with
 * different scores, to a PAB receiver, and to anyone who scored more than a
 * loss without playing; an upfloat to the lower-ranked player of such a pair.
 */
function floatIn(
  p: PairingPlayer,
  round: number,
  req: PairingRequest,
  byId: ReadonlyMap<number, PairingPlayer>,
): Float {
  const e = p.history[round - 1];
  if (e === undefined) return null;
  if (!isPlayedResult(e.result) || e.opponent === null) {
    return pointsFor(e.result, req.pointSystem) > req.pointSystem.loss ? 'down' : null;
  }
  const opp = byId.get(e.opponent);
  if (opp === undefined) return null;
  const mine = scoreBefore(p, round, req);
  const theirs = scoreBefore(opp, round, req);
  if (mine > theirs) return 'down';
  if (mine < theirs) return 'up';
  return null;
}

export function buildDutchPlayers(req: PairingRequest): DutchPlayer[] {
  const byId = new Map(req.players.map((p) => [p.id, p]));
  const forbidden = new Map<number, Set<number>>();
  for (const [a, b] of req.forbiddenPairs ?? []) {
    if (!forbidden.has(a)) forbidden.set(a, new Set());
    if (!forbidden.has(b)) forbidden.set(b, new Set());
    (forbidden.get(a) as Set<number>).add(b);
    (forbidden.get(b) as Set<number>).add(a);
  }
  const ps = req.pointSystem;
  const played = req.round - 1;
  const topThreshold = (played * Math.max(ps.win, ps.draw)) / 2;
  const lastRound = played >= req.totalRounds - 1;
  const out: DutchPlayer[] = [];
  for (const p of req.players) {
    if (!p.available) continue;
    const cs = colourState(p.history);
    let points = 0;
    let pabEligible = true;
    let playedGames = 0;
    const cannotMeet = new Set<number>(forbidden.get(p.id) ?? []);
    for (const e of p.history) {
      const pts = pointsFor(e.result, ps);
      points += pts;
      const wasPlayed = isPlayedResult(e.result) && e.opponent !== null;
      if (wasPlayed) {
        playedGames++;
        cannotMeet.add(e.opponent as number);
      } else if (e.result === 'U' || pts >= ps.win) {
        pabEligible = false;
      }
    }
    const score = points + (p.acceleration ?? 0);
    const c = cs.colours;
    const last = c[c.length - 1];
    const repeatedColour = last !== undefined && c[c.length - 2] === last ? last : null;
    out.push({
      id: p.id,
      idx: 0,
      score,
      rating: p.rating,
      colours: c,
      colourDifference: cs.difference,
      preference: cs.preference,
      strength: cs.strength,
      absoluteImbalance: Math.abs(cs.difference) > 1,
      repeatedColour,
      float1: played >= 1 ? floatIn(p, played, req, byId) : null,
      float2: played >= 2 ? floatIn(p, played - 1, req, byId) : null,
      pabEligible,
      playedGames,
      cannotMeet,
      topscorer: lastRound && score > topThreshold,
    });
  }
  out.sort((a, b) => b.score - a.score || a.id - b.id);
  out.forEach((p, i) => (p.idx = i));
  return out;
}

/** Absolute criteria [C1] and [C3]: may these two players meet this round? */
export function compatible(a: DutchPlayer, b: DutchPlayer): boolean {
  if (a.cannotMeet.has(b.id) || b.cannotMeet.has(a.id)) return false;
  if (
    a.strength === 'absolute' &&
    b.strength === 'absolute' &&
    a.preference === b.preference &&
    !a.topscorer &&
    !b.topscorer
  ) {
    return false;
  }
  return true;
}
